import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import JsonLd, { breadcrumb, floorPlanSchema } from "@/components/JsonLd";
import { ArrowLeft, ArrowRight, Ruler, BedDouble, Bath, Car, Layers, CheckCircle2 } from "lucide-react";

export default function FloorPlanDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get(`/floor-plans/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === null) return <PublicLayout><div className="max-w-7xl mx-auto px-5 py-20 text-sm" style={{color:'var(--text-muted)'}}>Loading…</div></PublicLayout>;
  if (p === false) return <PublicLayout><div className="max-w-7xl mx-auto px-5 py-20"><h1 className="font-display text-3xl">Plan not found</h1><Link to="/floor-plans" className="btn-ghost mt-4">Browse plans</Link></div></PublicLayout>;

  return (
    <PublicLayout>
      <JsonLd id="bc-plan" data={breadcrumb([{name: "Home", url: "/"}, {name: "Floor Plans", url: "/floor-plans"}, {name: p.name, url: `/floor-plans/${p.id}`}])} />
      <JsonLd id="plan-detail" data={floorPlanSchema(p)} />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12">
        <Link to="/floor-plans" className="inline-flex items-center gap-2 text-sm mb-8" style={{color:'var(--text-muted)'}}>
          <ArrowLeft size={14}/> All floor plans
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 pb-12">
        <div className="lg:col-span-7">
          <div className="rounded-2xl overflow-hidden aspect-[16/10] mb-4">
            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {p.plan_image_url && (
            <div className="rounded-2xl overflow-hidden aspect-[16/9]" style={{background:'#fff'}}>
              <img src={p.plan_image_url} alt={`${p.name} blueprint`} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <div className="badge mb-4">Floor Plan</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-4">{p.name}</h1>
          <div className="flex items-baseline gap-2 mb-6">
            <span className="text-xs uppercase tracking-widest" style={{color:'var(--text-muted)'}}>Starting at</span>
            <span className="font-display text-3xl font-bold" style={{color:'var(--accent)'}}>{money(p.price_from)}</span>
          </div>
          <p className="text-base leading-relaxed mb-8" style={{color:'var(--text-muted)'}}>{p.description}</p>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <SpecCard icon={Ruler} label="Square feet" value={p.sqft.toLocaleString()} />
            <SpecCard icon={Layers} label="Stories" value={p.stories} />
            <SpecCard icon={BedDouble} label="Bedrooms" value={p.bedrooms} />
            <SpecCard icon={Bath} label="Bathrooms" value={p.bathrooms} />
            <SpecCard icon={Car} label="Garage" value={`${p.garage}-car`} />
          </div>

          {p.features && p.features.length > 0 && (
            <div className="mb-8">
              <div className="label-overline mb-3">Features</div>
              <ul className="space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm">
                    <CheckCircle2 size={16} style={{color:'var(--accent)'}} className="shrink-0 mt-0.5"/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Link to={`/contact?service=floor_plan&plan=${p.id}`} className="btn-primary w-full justify-center" data-testid="plan-cta-quote">
            Request this Plan <ArrowRight size={16}/>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}

function SpecCard({ icon: Icon, label, value }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-1.5 text-xs uppercase tracking-wider" style={{color:'var(--text-muted)'}}>
        <Icon size={13} style={{color:'var(--accent)'}}/> {label}
      </div>
      <div className="font-display text-2xl font-bold">{value}</div>
    </div>
  );
}
